/**
 * Client-side product availability — mirrors backend rules for UI and listing filters.
 * Server validation remains authoritative (cart, checkout, offers).
 */

const VIEWABLE_STATUSES = new Set(['ACTIVE', 'OUT_OF_STOCK']);

export function isVariableProductType(productType) {
  return productType === 'variable' || productType === 'variant';
}

export function getVariantStock(variant) {
  if (!variant) return 0;
  return Math.max(0, Number(variant.quantityAvailable ?? variant.stock) || 0);
}

export function sumVariantStock(variants = []) {
  if (!Array.isArray(variants)) return 0;
  return variants
    .filter((v) => v && v.isActive !== false)
    .reduce((sum, v) => sum + getVariantStock(v), 0);
}

export function getTotalStock(product) {
  if (!product) return 0;

  const variantList = product.variants ?? [];

  if (isVariableProductType(product.productType)) {
    const fromVariants = sumVariantStock(variantList);
    if (fromVariants > 0) return fromVariants;
    if (typeof product.totalStock === 'number' && product.totalStock > 0) {
      return product.totalStock;
    }
    // Never use parent quantityAvailable for variant/variable rows (often 0).
    return 0;
  }

  return Math.max(
    0,
    Number(
      product.quantityAvailable ??
        product.totalStock ??
        product.stock ??
        product.quantity
    ) || 0
  );
}

/** Stock to show in UI (selected SKU or product total). */
export function getDisplayStock(product, selectedVariant = null) {
  if (!product) return 0;
  if (selectedVariant) return getVariantStock(selectedVariant);
  return getTotalStock(product);
}

export function findAvailableVariant(product, size, colorName) {
  if (!product?.variants?.length) return null;
  if (!size && !colorName) return null;

  return (
    product.variants.find((v) => {
      if (v.isActive === false) return false;
      if (size && v.size !== size) return false;
      if (colorName && v.color !== colorName) return false;
      return getVariantStock(v) > 0;
    }) ?? null
  );
}

function isSellerValid(product) {
  const owner = product?.owner;
  if (!owner) return false;
  const status = (owner.status ?? 'active').toString().toLowerCase();
  return status === 'active' || status === '';
}

export function deriveAvailabilityStatus(product) {
  if (!product) return 'INVALID';

  if (product.deletedAt) return 'DELETED';

  const stored = product.availabilityStatus;
  if (stored === 'ARCHIVED') return 'ARCHIVED';
  if (stored === 'DELETED') return 'DELETED';
  if (stored === 'INVALID') return 'INVALID';

  if (!isSellerValid(product)) return 'INVALID';
  if (product.isActive === false) return 'HIDDEN';

  const stock = getTotalStock(product);
  if (stock <= 0) return 'OUT_OF_STOCK';

  return 'ACTIVE';
}

export function canDisplayProduct(product) {
  if (product?.canDisplay === true) return true;
  if (product?.canDisplay === false) return false;
  return deriveAvailabilityStatus(product) === 'ACTIVE';
}

export function canViewProductPage(product) {
  const status = deriveAvailabilityStatus(product);
  return VIEWABLE_STATUSES.has(status);
}

export function canBuyProduct(product) {
  if (product?.canBuy === true) return true;
  if (product?.canBuy === false) return false;
  return deriveAvailabilityStatus(product) === 'ACTIVE';
}

export function canOfferProduct(product) {
  if (product?.canOffer === true) return true;
  if (product?.canOffer === false) return false;
  return deriveAvailabilityStatus(product) === 'ACTIVE';
}

export function isProductInStock(product) {
  if (!product) return false;
  if (product.canBuy === true) return true;
  if (product.canBuy === false) return false;
  return getTotalStock(product) > 0;
}

export function isProductOutOfStock(product) {
  return !isProductInStock(product);
}

/** Hide from homepage, search, category, related, storefront listings. */
export function filterListableProducts(products) {
  if (!Array.isArray(products)) return [];
  return products.filter(canDisplayProduct);
}

export function getAvailabilityBadge(product) {
  const status = deriveAvailabilityStatus(product);
  if (status === 'OUT_OF_STOCK') return 'out_of_stock';
  if (status === 'ACTIVE') return 'in_stock';
  return 'unavailable';
}
